import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { Card, ToggleField, SaveBar, MissingConfigBanner } from '../components/Form';
import { useConfigDoc, useWriteConfig } from '../lib/configHooks';
import { useToast } from '../components/Toast';

const KNOWN_FLAGS: Array<{ key: string; label: string; hint?: string }> = [
  {
    key: 'enableScheduledOrders',
    label: 'Enable scheduled orders',
    hint: 'Customers can book pickups for a future time.',
  },
  {
    key: 'enableInAppChat',
    label: 'Enable in-app chat',
    hint: 'Customer ↔ driver real-time messaging.',
  },
  {
    key: 'enableDriverEarningsScreen',
    label: 'Show driver earnings screen',
  },
  {
    key: 'enableSurgeBanner',
    label: 'Show surge banner to customers',
  },
  {
    key: 'requireDriverPhoto',
    label: 'Require proof-of-delivery photo',
  },
  {
    key: 'maintenanceMode',
    label: 'Maintenance mode (block new orders)',
    hint: 'Customers see a maintenance screen; existing orders continue.',
  },
];

export default function FeatureFlagsPage() {
  const { data } = useConfigDoc<Record<string, boolean>>('feature_flags');
  const write = useWriteConfig('feature_flags');
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const [dirty, setDirty] = useState(false);
  const [customKey, setCustomKey] = useState('');
  const toast = useToast();

  useEffect(() => {
    if (!dirty) setDraft(data || {});
  }, [data, dirty]);

  const setFlag = (k: string, v: boolean) => {
    setDraft((d) => ({ ...d, [k]: v }));
    setDirty(true);
  };

  const onSave = async () => {
    // Strip server-side metadata that lives on the doc but isn't a flag.
    // The Firestore document includes `updatedAt` (Timestamp) and
    // `updatedBy` (uid string), neither of which pass the
    // record<string, boolean> schema — they would silently fail the
    // callable validation and the page would appear "stuck dirty".
    const cleaned: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(draft)) {
      if (typeof v === 'boolean') cleaned[k] = v;
    }
    try {
      await write.mutateAsync(cleaned);
      setDraft(cleaned);
      setDirty(false);
      toast.success('Feature flags saved.');
    } catch (e) {
      toast.error(`Save failed: ${e instanceof Error ? e.message : e}`);
    }
  };

  const customKeys = Object.keys(draft).filter(
    (k) => !KNOWN_FLAGS.some((f) => f.key === k)
  );

  return (
    <>
      <PageHeader
        title="Feature Flags"
        subtitle="Toggle product features without an app release."
      />
      {data && Object.keys(data).length === 0 && (
        <MissingConfigBanner docIds={['feature_flags']} />
      )}
      <Card title="Standard flags">
        {KNOWN_FLAGS.map((f) => (
          <ToggleField
            key={f.key}
            label={f.label}
            hint={f.hint}
            value={!!draft[f.key]}
            onChange={(v) => setFlag(f.key, v)}
          />
        ))}
      </Card>
      {customKeys.length > 0 && (
        <Card title="Custom flags">
          {customKeys.map((k) => (
            <ToggleField
              key={k}
              label={k}
              value={!!draft[k]}
              onChange={(v) => setFlag(k, v)}
            />
          ))}
        </Card>
      )}
      <Card title="Add custom flag">
        <div className="flex gap-2">
          <input
            type="text"
            value={customKey}
            onChange={(e) => setCustomKey(e.target.value)}
            placeholder="flag_key_name"
            className="flex-1 rounded border border-slate-300 px-3 py-1.5 text-sm"
          />
          <button
            type="button"
            onClick={() => {
              if (!customKey.trim()) return;
              setFlag(customKey.trim(), false);
              setCustomKey('');
            }}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            Add
          </button>
        </div>
      </Card>
      <SaveBar
        dirty={dirty}
        saving={write.isPending}
        onSave={onSave}
        onReset={() => {
          setDraft(data || {});
          setDirty(false);
        }}
      />
    </>
  );
}
